import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/services/utils/cn'

interface RangePickerProps {
  defaultMin: number
  defaultMax: number
  isActive?: boolean
  onChange?: (values: { min: number; max: number }) => void
  step?: number
  className?: string
}

export function RangePicker({
  defaultMin = 0,
  defaultMax,
  isActive = true,
  onChange,
  step = 1,
  className,
}: RangePickerProps) {
  const [rangeValues, setRangeValues] = useState<[number, number]>([
    defaultMin,
    defaultMax,
  ])

  const min = rangeValues[0]
  const max = rangeValues[1]

  // Handle slider change
  const handleSliderChange = (values: number[]) => {
    setRangeValues([values[0] ?? defaultMin, values[1] ?? defaultMax])
    onChange?.({ min: values[0] ?? defaultMin, max: values[1] ?? defaultMax })
  }

  // Handle min input change
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = Number(e.target.value.replace(/,/g, ''))
    if (isNaN(numericValue)) return

    const newMin = numericValue
    let newMax = max

    // If min exceeds max, update max as well
    if (numericValue > max) {
      newMax = numericValue
    }

    setRangeValues([newMin, newMax])
    onChange?.({ min: newMin, max: newMax })
  }

  // Handle max input change
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = Number(e.target.value.replace(/,/g, ''))
    if (isNaN(numericValue)) return

    let newMin = min
    const newMax = numericValue

    // If max is less than min, update min as well
    if (numericValue < min) {
      newMin = numericValue
    }

    setRangeValues([newMin, newMax])
    onChange?.({ min: newMin, max: newMax })
  }

  return (
    <div className={cn(`space-y-4`, className)}>
      <div className="py-3">
        <Slider
          defaultValue={[defaultMin, defaultMax]}
          max={defaultMax}
          step={step}
          value={rangeValues}
          onValueChange={handleSliderChange}
          disabled={!isActive}
        />
      </div>
      <div className="flex justify-between gap-[9px] text-[10px]">
        {/* Min Value */}
        <div className="border-zinc200 flex h-[35px] w-[184px] rounded-md border px-3 py-2">
          <h1 className="mr-2 font-bold">Min</h1>
          <Input
            type="text"
            inputMode="numeric"
            className={cn(
              'h-auto w-full rounded-none border-0 bg-transparent p-0 text-sm outline-none',
              isActive ? 'text-black' : 'text-zinc-500'
            )}
            value={min.toLocaleString()}
            onChange={handleMinChange}
            disabled={!isActive}
          />
        </div>
        {/* Max Value */}
        <div className="border-zinc200 flex h-[35px] w-[184px] rounded-md border px-3 py-2">
          <h1 className="mr-2 font-bold">Max</h1>
          <Input
            type="text"
            inputMode="numeric"
            className={cn(
              'h-auto w-full rounded-none border-0 bg-transparent p-0 text-sm outline-none',
              isActive ? 'text-black' : 'text-zinc-500'
            )}
            value={max.toLocaleString()}
            onChange={handleMaxChange}
            disabled={!isActive}
          />
        </div>
      </div>
    </div>
  )
}
